import React, { Fragment, ReactNode } from 'react'
import { Box, Typography } from '@mui/material'
import { Lock as LockIcon } from '@mui/icons-material'
import {
  ViewMode,
  MapViewMode,
  HATCH_BACKGROUND,
  MULTI_BRAND_COLOR,
  CELL_ALPHA,
  EMPTY_CELL_BG,
  EMPTY_CELL_BG_OCCUPANCY,
  OCCUPANCY_SCALE,
} from '../config/constants'
import { occupancyRangeLabel, withAlpha } from '../config/utils'
import { mapLegendStyles as styles } from '../styles/MapLegend.styles'
import { slotMarkerSx } from '../styles/shared.styles'

// "Sin stock" no está acá: vive en el bloque de estados de la derecha, que es
// idéntico en los tres modos. Estos son sólo los tramos con stock, derivados de
// `OCCUPANCY_SCALE` para que la leyenda no pueda anunciar un corte que la celda
// ya no usa.
const OCCUPANCY_RANGES = OCCUPANCY_SCALE.map((range, i) => ({
  label: occupancyRangeLabel(i),
  color: range.color,
}))

function LegendItem({ label, children }: { label: string; children: ReactNode }) {
  return (
    <Box sx={styles.item}>
      {children}
      <Typography variant='caption'>{label}</Typography>
    </Box>
  )
}

/**
 * Recuadro de muestra. `fill` acepta tanto un color como el gradiente del
 * rayado, así que se aplica con `background` y no con `bgcolor`.
 */
function Swatch({ fill, children }: { fill: string; children?: ReactNode }) {
  return <Box sx={styles.swatch(fill)}>{children}</Box>
}

/** Los tramos y las marcas se muestran con la misma transparencia que las celdas. */
const asCellFill = (color: string) => withAlpha(color, CELL_ALPHA)

/**
 * Leyenda del mapa, compartida por la grilla principal y la vista de pasillo.
 *
 * Dos bloques con un separador: a la izquierda las marcas (o zonas, o rangos de
 * ocupación) según el modo activo; a la derecha los estados de la posición,
 * siempre en el mismo orden. Un único componente para las dos vistas evita que
 * el orden vuelva a divergir entre ellas.
 */
export function MapLegend({
  mode,
  brandColors,
  zoneColors,
}: {
  mode: ViewMode
  brandColors: Map<string, string>
  zoneColors: Map<string, string>
}) {
  let marks: ReactNode

  if (mode === MapViewMode.occupancy) {
    marks = OCCUPANCY_RANGES.map(({ label, color }) => (
      <LegendItem key={label} label={label}>
        <Swatch fill={asCellFill(color)} />
      </LegendItem>
    ))
  } else {
    const isZone = mode === MapViewMode.zone
    const entries = Array.from((isZone ? zoneColors : brandColors).entries())
    marks = (
      <Fragment>
        {entries.map(([name, color]) => (
          <LegendItem key={name} label={name}>
            <Swatch fill={asCellFill(color)} />
          </LegendItem>
        ))}
        <LegendItem label={isZone ? 'Multi-zona' : 'Multi-marca'}>
          <Swatch fill={asCellFill(MULTI_BRAND_COLOR)} />
        </LegendItem>
      </Fragment>
    )
  }

  return (
    <Box sx={styles.root}>
      {marks}

      <Box sx={styles.divider} />

      <LegendItem label='Sin stock'>
        {/* En ocupación la celda vacía es gris; en los otros modos, blanca. */}
        <Swatch
          fill={asCellFill(
            mode === MapViewMode.occupancy ? EMPTY_CELL_BG_OCCUPANCY : EMPTY_CELL_BG
          )}
        >
          <Typography sx={styles.emptyMarker}>0</Typography>
        </Swatch>
      </LegendItem>

      <LegendItem label='Sin configurar'>
        <Swatch fill={HATCH_BACKGROUND} />
      </LegendItem>

      <LegendItem label='Bloqueada'>
        <Swatch fill={EMPTY_CELL_BG}>
          <LockIcon sx={styles.lockIcon} />
        </Swatch>
      </LegendItem>

      <LegendItem label='Con slots'>
        <Swatch fill={EMPTY_CELL_BG}>
          <Box sx={slotMarkerSx(7)} />
        </Swatch>
      </LegendItem>
    </Box>
  )
}
